/**
 * Composer / thread helpers for the WhatsApp channel (ADR-025).
 *
 * Pure functions so the fallback ladder and botmap resolution can be unit
 * tested without mounting the composer.
 */
import {
  Channel,
  ContactChannel,
  getAvailableChannels,
  threadChannel,
} from '../../../utils/channels';

export type WhatsAppFallback = 'sms' | 'template' | 'hold';

export interface SendBotmapResolution {
  botmapId?: string;
  /** Address the message is sent `to` (mobile for SMS, WhatsApp address otherwise). */
  to?: string;
  /** errorMessages key when the channel cannot be used. */
  error?: 'noTextingNumber' | 'noWhatsAppNumber';
}

/** The user's own 1-1 texting line (nexmo longcode) from `userData.bots`. */
export function findSmsLongcode(userBots: any[] | undefined | null): any | undefined {
  if (!Array.isArray(userBots)) return undefined;
  return userBots.find((bot: any) => bot && bot.type === 'nexmo' && bot.default == 'longcode'); // eslint-disable-line eqeqeq
}

export function getWhatsAppChannel(contactModel: any): ContactChannel | undefined {
  return getAvailableChannels(contactModel).find((c) => c.type === 'whatsapp');
}

/**
 * Which botmap (and destination) a send on `channel` should use.
 *   sms      → the user's nexmo longcode + the contact's mobile
 *   whatsapp → the company WhatsApp sender from `contactModel.channels`
 */
export function resolveSendBotmap(
  channel: Channel,
  userBots: any[] | undefined | null,
  contactModel: any,
): SendBotmapResolution {
  if (channel === 'whatsapp') {
    const wa = getWhatsAppChannel(contactModel);
    if (!wa || !wa.botmapId) return { error: 'noWhatsAppNumber' };
    return { botmapId: String(wa.botmapId), to: wa.address || contactModel?.whatsapp || contactModel?.mobile };
  }
  const longcode = findSmsLongcode(userBots);
  if (!longcode) return { error: 'noTextingNumber' };
  return { botmapId: String(longcode.botmap_id), to: contactModel?.mobile };
}

const toTime = (value?: string | null): number => {
  if (!value) return 0;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

/**
 * Most recent inbound (non-scheduled) thread, optionally restricted to a
 * channel and to rows created after `sinceMs`.
 */
export function latestInboundThread(
  threads: any[] | undefined | null,
  channel?: Channel,
  sinceMs?: number,
): any | null {
  if (!Array.isArray(threads)) return null;
  let best: any = null;
  threads.forEach((t) => {
    if (!t || t.direction !== 'inbound') return;
    if (channel && threadChannel(t) !== channel) return;
    if (sinceMs != null && toTime(t.created_at) <= sinceMs) return;
    if (!best || toTime(t.created_at) > toTime(best.created_at)) best = t;
  });
  return best;
}

/**
 * Last inbound WhatsApp timestamp: what the contact model reported, moved
 * forward by any inbound WhatsApp thread pushed over the socket since the
 * model was loaded (`sinceMs`). Without `sinceMs` every loaded thread counts.
 */
export function effectiveLastInboundAt(
  waChannel: ContactChannel | undefined | null,
  threads: any[] | undefined | null,
  sinceMs?: number,
): string | null {
  const fromModel = waChannel?.lastInboundAt || null;
  const latest = latestInboundThread(threads, 'whatsapp', sinceMs);
  const fromThreads = latest?.created_at || null;
  if (!fromModel) return fromThreads;
  if (!fromThreads) return fromModel;
  return toTime(fromThreads) > toTime(fromModel) ? fromThreads : fromModel;
}

/**
 * Channel the composer should start on: the channel of the latest inbound
 * thread when the contact can be reached on it, else SMS.
 */
export function defaultComposerChannel(threads: any[] | undefined | null, channels: ContactChannel[]): Channel {
  const latest = latestInboundThread(threads);
  const latestChannel = latest ? threadChannel(latest) : null;
  if (latestChannel && channels.some((c) => c.type === latestChannel)) return latestChannel;
  return 'sms';
}

export interface FallbackAvailability {
  sms: boolean;
  template: boolean;
  hold: boolean;
}

export function fallbackAvailability(opts: {
  hasMobile: boolean;
  optedOut: boolean;
  hasSmsBotmap: boolean;
}): FallbackAvailability {
  return {
    sms: !!opts.hasMobile && !opts.optedOut && !!opts.hasSmsBotmap,
    template: true,
    hold: true,
  };
}

/** First enabled option in the fixed order SMS → template → hold. */
export function defaultFallback(availability: FallbackAvailability): WhatsAppFallback {
  if (availability.sms) return 'sms';
  if (availability.template) return 'template';
  return 'hold';
}

export const FALLBACK_SEND_LABEL: Record<WhatsAppFallback, string> = {
  sms: 'Send as SMS',
  template: 'Send template',
  hold: 'Hold',
};

/** `thread.meta` arrives as a JSON string on list rows; parse defensively. */
export function parseThreadMeta(item: any): Record<string, any> {
  if (!item) return {};
  const { meta } = item;
  if (!meta) return {};
  if (typeof meta === 'object') return meta;
  if (typeof meta === 'string') {
    try {
      const parsed = JSON.parse(meta);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

/** 'template' | 'freeform' | null — the backend lifts `via` onto some rows only. */
export function threadVia(item: any): string | null {
  if (!item) return null;
  if (item.via) return String(item.via);
  const meta = parseThreadMeta(item);
  return meta.via ? String(meta.via) : null;
}

/** Infobip rejects a free-form WhatsApp message outside the 24h window with 7010. */
export const WHATSAPP_WINDOW_CLOSED_CODE = '7010';

export function isWindowClosedRejection(item: any): boolean {
  return !!item && String(item.provider_code ?? '') === WHATSAPP_WINDOW_CLOSED_CODE;
}

// ── Closed-window template option (ADR-037 phase 0B-1) ──────────────

/**
 * Sub-text under the composer's "Send WhatsApp template" option. A template
 * with no slot for the recruiter's text (`hasTextParam === false`) sends the
 * notice now and holds the typed message until the candidate replies.
 */
export function templateFallbackHint(hasTextParam: boolean | undefined | null, firstName: string): string {
  if (hasTextParam === false) {
    return `Sends this notice now. Your message is held and sent when ${firstName || 'the candidate'} replies.`;
  }
  return 'Meta-approved utility template. Your text becomes the message body. Billed per message.';
}

/** Toast key for a send result: a template sent with the text held reads differently from a plain hold. */
export function sendResultToastKey(
  result: { held?: boolean; via?: string | null; channelUsed?: string | null } | null | undefined,
  requestedChannel: string,
): 'sentTemplateHeld' | 'heldForReply' | 'sentTemplate' | 'sentWhatsApp' | 'sentSmsFallback' | 'messageSent' {
  const r = result || {};
  if (r.held && r.via === 'template') return 'sentTemplateHeld';
  if (r.held) return 'heldForReply';
  if (r.channelUsed === 'whatsapp') return r.via === 'template' ? 'sentTemplate' : 'sentWhatsApp';
  if (requestedChannel === 'whatsapp' && r.channelUsed === 'sms') return 'sentSmsFallback';
  return 'messageSent';
}

/** Marker the backend renders in the text slot of a preview (`sampleCtx.text`). */
export const TYPED_TEXT_MARKER = '[your message]';

/**
 * The window endpoint's template preview with the recruiter's typed text in
 * the text slot: the server's `[your message]` marker, or any `{{n}}` left open.
 */
export function renderWindowTemplatePreview(preview: string, typed: string): string {
  const text = String(typed || '').trim() || '…';
  return String(preview || '')
    .split(TYPED_TEXT_MARKER).join(text)
    .replace(/\{\{\s*\d+\s*\}\}/g, () => text);
}
